import {
  CRITERION_FICHE_WIDTH,
  CriterionCardPopover,
} from '@/components/admin/protocoles/step/criterion-card-popover';
import { VARIABLE_IDS } from '@/components/admin/variables/contracts';
import { VariablePill } from '@/components/admin/variables/variable-pill';
import { useFloatingPlacement } from '@/lib/use-floating-placement';
import type { FicheContent, FicheLink, FichePill } from '@/lib/variable-fiche';
import { ficheContentOf, ficheName } from '@/lib/variable-fiche';
import type { VariableStanding, ZonePlace } from '@/lib/variable-pill';
import { pillOf } from '@/lib/variable-pill';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

/**
 * La fiche d'une pastille (refonte modulaire §8.6 ; grill Variables du
 * 2026-09-16, D6 ; maquette `variables.html`, planche 3 ; issue #88).
 *
 * « Une fiche dit sa nature, sa provenance, ce qu'elle rend à cet endroit, et
 * offre un lien Ouvrir ; elle ne porte aucune action. » What it says is
 * `ficheContentOf`, pure; what this file adds is the box, where it stands, and
 * the one case the pure module hands over: a CRITERION, whose fiche is the
 * popover the protocol editor already opens on the line of an objective,
 * mounted here as it is (D6, « la fiche du critère est celle qui existe
 * déjà »).
 *
 * ## Fixed, beside its pill, and flipped above when the window ends
 *
 * The pill may sit inside a `contentEditable` that scrolls inside its own
 * frame, so the fiche cannot be a child of the zone: `overflow` would clip it.
 * It is `position: fixed`, placed from the pill's `getBoundingClientRect` in a
 * layout effect - measured, because whether it fits under the pill depends on
 * its own height, which only the DOM knows - and it follows the window's
 * scroll and resize while it is open.
 *
 * ## The link needs a router, the criterion needs a client
 *
 * « Ouvrir la page N → » is a `Link` to the page editor and « Ouvrir le
 * protocole ↗ » a `Link` to the protocol's editor: both are routes of the
 * shell, so a zone that mounts a pill has to sit under a router. The
 * criterion's popover reads the base through `useQuery` and `useAdminSession`,
 * and it is reached by a hover: the régime `criterion-requirement-row.tsx`
 * states - « la fiche d'un critère est montée seulement quand elle s'ouvre » -
 * is what lets a step editor mount without a `QueryClientProvider`.
 *
 * ## The criterion's fiche now says where it stands, and here it is US (#104)
 *
 * It used to position itself `absolute top-full left-0`, and this file
 * exploited that: a positioned wrapper of NO height dropped it exactly where
 * the box would have been. Issue #104 took that away - the fiche is `fixed` and
 * placed on the control that opens it, because in a protocol's step editor it
 * was being cropped by the scroll above it.
 *
 * So it takes its anchor as a prop, and D6's intent survives the change
 * untouched: « une fiche pour un critère, où qu'on la lise ». Passing NO anchor
 * is this file saying « I have already placed you » - the box below is the one
 * that is `fixed`, measured, followed through scroll and resize, and named
 * `role="note"`; the fiche draws its card inside it and claims nothing.
 *
 * The one thing that had to follow is the WIDTH. The box was 340 px and the
 * criterion's card 380: while the card was `absolute` it simply overhung its
 * box by 40 px, and the placement was computed on a rectangle 40 px narrower
 * than what was drawn - so a pill near the right edge put the card partly out
 * of the window. In the flow the card fills its box, so the box takes the
 * card's own width and the placement is finally computed on what is painted.
 */

export interface VariableFicheProps {
  readonly standing: VariableStanding;
  /**
   * Where the pill stands, for « Rend ici » (D4); null in a protocol's own
   * direction, `READING_PLACE` in its reading for the analyst (#158).
   */
  readonly place: ZonePlace | null;
  /** The experience the page links open in, or null before its first save. */
  readonly experienceId: string | null;
  readonly anchor: HTMLElement;
  readonly pinned: boolean;
  readonly ficheRef: (node: HTMLElement | null) => void;
}

const FICHE_WIDTH = 340;

const PILL_TONE_CLASS: Readonly<Record<FichePill['tone'], string>> = {
  objective: 'bg-bleu-nuit-tint text-bleu-nuit',
  production: 'bg-vert-tint text-vert-deep',
  development: 'bg-bleu-ciel-tint text-bleu-ciel-deep',
  neutral: 'bg-gray-100 text-gray-600',
};

function OpenLink({
  link,
  experienceId,
}: {
  readonly link: FicheLink;
  readonly experienceId: string | null;
}): ReactNode {
  const className =
    'mt-2.5 inline-flex items-center gap-1 font-semibold text-bleu-nuit text-small underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit';
  if (link.kind === 'protocol') {
    return (
      <Link
        to="/admin/protocoles/$protocolId"
        params={{ protocolId: link.protocolId }}
        target="_blank"
        data-testid={VARIABLE_IDS.ficheOpen}
        className={className}
      >
        {link.label}
      </Link>
    );
  }
  // A page of an experience that has never been saved has no address yet.
  if (experienceId === null) return null;
  return (
    <Link
      to="/admin/experiences/$experienceId/pages/$pageId"
      params={{ experienceId, pageId: link.pageId }}
      data-testid={VARIABLE_IDS.ficheOpen}
      className={className}
    >
      {link.label}
    </Link>
  );
}

function Body({
  content,
  experienceId,
}: {
  readonly content: FicheContent;
  readonly experienceId: string | null;
}): ReactNode {
  return (
    <>
      {content.pills.length === 0 ? null : (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {content.pills.map((pill) => (
            <span
              key={pill.label}
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-semibold text-caption leading-4 ${PILL_TONE_CLASS[pill.tone]}`}
            >
              {pill.label}
            </span>
          ))}
        </div>
      )}
      {content.rows.length === 0 ? null : (
        <dl className="mt-2.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-small leading-5">
          {content.rows.map((row) => (
            <div key={row.term} className="contents">
              <dt className="whitespace-nowrap text-gray-500 text-caption leading-5">{row.term}</dt>
              <dd className="m-0 text-gray-800">{row.detail}</dd>
            </div>
          ))}
        </dl>
      )}
      {content.direction === null ? null : (
        <p className="mt-2 text-gray-700 text-small italic leading-5">« {content.direction} »</p>
      )}
      {content.renders === null ? null : (
        <p className="mt-2.5 rounded-sm bg-gray-50 px-2.5 py-2 text-gray-700 text-small leading-5">
          {content.renders}
        </p>
      )}
      {content.warning === null ? null : (
        <p className="mt-2.5 rounded-sm bg-jaune-tint px-2.5 py-2 text-jaune-deep text-small leading-5">
          {content.warning}
        </p>
      )}
      {content.refusal === null ? null : (
        <p className="mt-2.5 rounded-sm bg-rouge-tint px-2.5 py-2 text-rouge-deep text-small leading-5">
          {content.refusal}
        </p>
      )}
      {content.link === null ? null : <OpenLink link={content.link} experienceId={experienceId} />}
    </>
  );
}

export function VariableFiche({
  standing,
  place,
  experienceId,
  anchor,
  pinned,
  ficheRef,
}: VariableFicheProps) {
  /*
    Read BEFORE the placement and not after it: a criterion's fiche is wider
    than every other, and a box placed on the wrong width is a box that can run
    off the right edge of the window.
  */
  const paint = pillOf(standing);
  const content = ficheContentOf(standing, place);
  const criterion =
    content === null && standing.facts?.kind === 'criterion' ? standing.variable.id : null;
  const width = criterion === null ? FICHE_WIDTH : CRITERION_FICHE_WIDTH;

  /*
    The placement is the shared one (issue 104, second review), and this file is
    the reason the module exists: it carried its own copy of the effect, the
    copy was missing the `ResizeObserver` that the criterion's fiche had just
    gained, and this lot had made the gap REACHABLE - the card used to sit in a
    wrapper of zero height, so nothing here ever flipped or clamped; it is in
    the flow now, and the height it is measured at is the height of « Lecture du
    critère… ». A pill near the bottom of the window then put « Ouvrir le
    critère » out of reach, in `position: fixed`, with nothing to bring it back.
  */
  const { at, panelRef } = useFloatingPlacement({
    anchor,
    width,
    side: 'bottom',
    align: 'start',
  });

  return (
    <div
      ref={(node) => {
        panelRef(node);
        ficheRef(node);
      }}
      /*
        `note` and not `dialog` (D6): a dialog promises modality and a focus
        trap, and this fiche opens on HOVER, leaves the focus where it was and
        closes on a click outside. Nor a `tooltip`, which may not hold
        interactive content - this one carries « Ouvrir ». `note` is what it
        IS, « ancillary to the main content », and it is the one fitting role
        with no HTML element behind it, so it names the fiche without claiming
        to be a `<dialog>` or a `<fieldset>`.
      */
      role="note"
      aria-label={ficheName(standing)}
      data-testid={VARIABLE_IDS.fiche}
      data-pinned={pinned}
      style={{ left: at.left, top: at.top, width }}
      className="fixed z-40 max-w-[calc(100vw-16px)] text-left"
    >
      {criterion !== null ? (
        /*
          No anchor, because this box IS the placement (see the header): the
          fiche of the protocol editor draws its card in the flow here, and the
          component is otherwise mounted untouched - one fiche for a criterion,
          wherever it is read (D6).
        */
        <CriterionCardPopover criterionId={criterion} />
      ) : (
        <div className="rounded-md border-2 border-gray-200 bg-white p-3.5 shadow-md">
          <div className="flex flex-wrap items-center gap-2">
            <VariablePill paint={paint} interactive={false} className="text-small leading-5" />
            {content === null ? null : (
              <span className="text-gray-600 text-caption leading-4">{content.kind}</span>
            )}
          </div>
          {content === null ? null : <Body content={content} experienceId={experienceId} />}
        </div>
      )}
    </div>
  );
}
